import React, { PropsWithChildren, useMemo } from 'react';

import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import type { Identifiers } from '@atlaskit/feature-gate-js-client/types';
import {
	FeatureGateEnvironment,
	PerimeterType,
	type OptionsWithDefaults,
	type ClientOptions,
} from '@atlaskit/feature-gate-js-client/types';
import { FeatureGatesInitialization } from '@atlassian/feature-gates-react';

import { BootstrapResult } from './Bootstrap';

type StatSigInitProps = PropsWithChildren<BootstrapResult>;

// source: https://developer.atlassian.com/platform/frontend-feature-flags/resources/api-keys/
const API_KEY_STG_FEDRAMP = '0a4b48b0-e5b2-4ec4-8f55-f04e06da420a';
const API_KEY_PROD_FEDRAMP = 'f244f185-2e99-4075-affb-98ed075a48e0';
const API_KEY_STG_COMMERCIAL = '3f773076-8ed9-4b60-8812-03ddda908122';
const API_KEY_PROD_COMMERCIAL = '30bc64f5-3ff3-47ba-b78a-0175b187ef8d';

/**
 * This is directly copied from the atlassian-context package.
 * They don't export it, so we need to copy it here.
 */
function _getEnvironmentFromDomain(): 'PROD' | 'STG' {
	const hostname = globalThis.location?.hostname;
	if (!hostname) {
		return 'PROD';
	}

	if (hostname.match(/stg\.atlassian-us-gov-mod\.(com|net)/)) {
		return 'STG';
	}

	if (hostname.match(/atlassian-stg-fedm\.net/)) {
		return 'STG';
	}

	if (hostname.match(/atlassian-us-gov-mod\.(com|net)/)) {
		return 'PROD';
	}

	if (hostname.match(/stg(\.internal)?\.atlassian\.com/)) {
		return 'STG';
	}

	if (hostname.match(/jira-dev\.com/)) {
		return 'STG';
	}

	return 'PROD';
}

function getOptions(
	stagingApiKey: string,
	productionApiKey: string,
	perimeter: PerimeterType,
): OptionsWithDefaults<ClientOptions> {
	const env = _getEnvironmentFromDomain();
	const isStaging = env === 'STG';

	return {
		targetApp: 'teams-directory-frontend_web',
		apiKey: isStaging ? stagingApiKey : productionApiKey,
		environment: isStaging ? FeatureGateEnvironment.Staging : FeatureGateEnvironment.Production,
		perimeter,
	};
}

function getFedRampOptions(): OptionsWithDefaults<ClientOptions> {
	return getOptions(API_KEY_STG_FEDRAMP, API_KEY_PROD_FEDRAMP, PerimeterType.FEDRAMP_MODERATE);
}

function getCommercialOptions(): OptionsWithDefaults<ClientOptions> {
	return getOptions(API_KEY_STG_COMMERCIAL, API_KEY_PROD_COMMERCIAL, PerimeterType.COMMERCIAL);
}

export function StatSigInit({
	children,
	orgId,
	cloudId,
	accountId,
}: StatSigInitProps): React.JSX.Element {
	const featureGatesIdentifiers = useMemo<Identifiers>(
		() => ({
			atlassianAccountId: accountId,
			tenantId: cloudId,
			atlassianOrgId: orgId,
		}),
		[accountId, cloudId, orgId],
	);

	const options = isFedRamp() ? getFedRampOptions() : getCommercialOptions();

	return (
		<FeatureGatesInitialization options={options} identifiers={featureGatesIdentifiers}>
			{children}
		</FeatureGatesInitialization>
	);
}
